import { useState, useCallback, useMemo } from 'react';
import { functions } from '../lib/firebase/config';
import { httpsCallable } from 'firebase/functions';

export interface ReportFilters {
  status?: string;
  suburb?: string;
  council?: string;
  postcode?: string;
  tags?: string[];
  categories?: string[];
  latitude?: number;
  longitude?: number;
  radiusKm?: number;
  dateFrom?: Date;
  dateTo?: Date;
  sortBy?: 'submittedAt' | 'pickupDate' | 'viewCount' | 'updatedAt';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  startAfter?: any;
}

export interface SearchQuery {
  text?: string;
  status?: string;
  postcode?: string;
  tags?: string[];
  categories?: string[];
  dateFrom?: Date;
  dateTo?: Date;
  sortBy?: 'relevance' | 'submittedAt' | 'pickupDate' | 'viewCount';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  startAfter?: any;
}

export interface Report {
  id: string;
  title: string;
  description: string;
  status: 'in_review' | 'active' | 'going_soon' | 'gone' | 'rejected';
  location: {
    latitude: number;
    longitude: number;
    address: string;
    suburb: string;
    council: string;
    postcode: string;
    geohash: string;
  };
  images: Array<{
    url: string;
    altText?: string;
    orderIndex: number;
  }>;
  tags: string[];
  categories?: Array<{
    id: string;
    name: string;
    color?: string;
  }>;
  submittedBy: string;
  submittedAt: any;
  approvedAt?: any;
  viewCount: number;
  pickupDate?: any;
  createdAt: any;
  updatedAt: any;
}

export interface ReportsResult {
  reports: Report[];
  hasMore: boolean;
  lastDoc?: any;
  totalCount?: number;
}

export interface SearchResult {
  reports: Report[];
  hasMore: boolean;
  lastDoc?: any;
  searchMetadata: {
    query: string;
    resultsCount: number;
    searchTime: number;
  };
}

export interface FacetsResult {
  reports: Report[];
  facets: {
    statuses: { [key: string]: number };
    postcodes: { [key: string]: number };
    suburbs: { [key: string]: number };
    councils: { [key: string]: number };
    tags: { [key: string]: number };
  };
}



export const useReports = () => {
  const [reports, setReports] = useState<Report[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<any>(null);
  const [searchMetadata, setSearchMetadata] = useState<SearchResult['searchMetadata'] | null>(null);
  const [facets, setFacets] = useState<FacetsResult['facets'] | null>(null);


  const getReportsFunction = useMemo(() => httpsCallable(functions, 'getReports'), []);
  const searchReportsFunction = useMemo(() => httpsCallable(functions, 'searchReports'), []);
  const getReportsWithFacetsFunction = useMemo(() => httpsCallable(functions, 'getReportsWithFacets'), []);
  const getReportFunction = useMemo(() => httpsCallable(functions, 'getReport'), []);
  const updateReportStatusFunction = useMemo(() => httpsCallable(functions, 'updateReportStatus'), []);

  const fetchReports = useCallback(async (filters: ReportFilters = {}, append: boolean = false) => {
    setIsLoading(true);
    setError(null);

    try {
      const queryFilters = {
        ...filters,
        startAfter: append ? lastDoc : undefined
      };

      const { data } = await getReportsFunction(queryFilters);
      const result = data as ReportsResult;

      if (append) {
        setReports(prev => [...prev, ...result.reports]);
      } else {
        setReports(result.reports);
        setSearchMetadata(null); // Clear search metadata for regular fetch
        setFacets(null); // Clear facets for regular fetch
      }

      setHasMore(result.hasMore);
      setLastDoc(result.lastDoc);

    } catch (error) {
      console.error('Error fetching reports:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch reports';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [getReportsFunction]);

  const searchReports = useCallback(async (searchQuery: SearchQuery = {}, append: boolean = false) => {
    setIsLoading(true);
    setError(null);

    try {
      // Add pagination support for search
      const query = {
        ...searchQuery,
        startAfter: append ? lastDoc : undefined
      };

      const { data } = await searchReportsFunction(query);
      const result = data as SearchResult;

      if (append) {
        setReports(prev => [...prev, ...result.reports]);
      } else {
        setReports(result.reports);
      }

      setHasMore(result.hasMore);
      setLastDoc(result.lastDoc);
      setSearchMetadata(result.searchMetadata);
      setFacets(null); // Clear facets for search

    } catch (error) {
      console.error('Error searching reports:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to search reports';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [searchReportsFunction]);

  const fetchReportsWithFacets = useCallback(async (filters: Omit<ReportFilters, 'startAfter' | 'limit'> = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const { data } = await getReportsWithFacetsFunction(filters);
      const result = data as FacetsResult;

      setReports(result.reports);
      setFacets(result.facets);
      setHasMore(false); // Faceted search doesn't support pagination
      setLastDoc(null);
      setSearchMetadata(null); // Clear search metadata for faceted fetch

    } catch (error) {
      console.error('Error fetching reports with facets:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch reports with facets';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [getReportsWithFacetsFunction]);

  const loadMore = useCallback(async (filters?: ReportFilters, searchQuery?: SearchQuery) => {
    if (!hasMore || isLoading) return;

    if (searchQuery) {
      await searchReports(searchQuery, true);
    } else {
      await fetchReports(filters || {}, true);
    }
  }, [hasMore, isLoading, searchReports, fetchReports]);

  const fetchReport = useCallback(async (reportId: string): Promise<Report | null> => {
    try {
      const { data } = await getReportFunction({ reportId });
      return data as Report;
    } catch (error) {
      console.error('Error fetching report:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to fetch report';
      setError(errorMessage);
      return null;
    }
  }, [getReportFunction]);

  const updateReportStatus = useCallback(async (
    reportId: string,
    status: Report['status']
  ): Promise<boolean> => {
    try {
      await updateReportStatusFunction({ reportId, status });

      // Update the report in the local state
      setReports(prev => prev.map(report =>
        report.id === reportId
          ? { ...report, status, updatedAt: new Date() }
          : report
      ));

      return true;
    } catch (error) {
      console.error('Error updating report status:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to update report status';
      setError(errorMessage);
      return false;
    }
  }, [updateReportStatusFunction]);

  const markAsGone = useCallback(async (reportId: string): Promise<boolean> => {
    return updateReportStatus(reportId, 'gone');
  }, [updateReportStatus]);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearReports = useCallback(() => {
    setReports([]);
    setHasMore(true);
  }, []);

  return {
    reports,
    isLoading,
    error,
    hasMore,
    lastDoc,
    searchMetadata,
    facets,
    fetchReports,
    searchReports,
    fetchReportsWithFacets,
    loadMore,
    fetchReport,
    updateReportStatus,
    markAsGone,
    clearError,
    clearReports
  };
};